import { useContext, useEffect, useState, useCallback, useRef } from "react";
import Lock from "../../components/Atomos/Icons/LockOutlined";
import {
  getGroupsByPersonIdWithDetails,
  markMessageAsViewed,
} from "../../Services/Group";
import { getPersonById } from "../../Services/Person";
import AuthContext from "../../store/AuthContext";
import EventBus from "../../utils/EventBus";
import { EventBusValues } from "../../utils/EventBus";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { GROUP_TYPES, GroupType } from "../../Enums/GroupEnum";
import { normalizeRoleToKey } from "../../Enums/UserEnums";
import {
  MyWSGGroupData,
  IDetail,
  ISubgroup,
} from "../../interfaces/models/IGroup";
import GroupIcon from "../../components/ViewMode/GroupIconComponent";
import CustomScrollContainer from "../../components/Atomos/CustomScrollContainer";
import AllMyGroupsIcon from "../../assets/images&icons/all-groups.png";
import IndustryGroupsIcon from "../../assets/images&icons/industry-groups.png";
import SpecialtyGroupsIcon from "../../assets/images&icons/specialty-groups.png";
import PracticeGroupsIcon from "../../assets/images&icons/practice-groups.png";
import WorldServicesGroupIcon from "../../components/Atomos/Icons/WorldServicesGroupIcon";
import { LEGACY_MEMBER_URL } from "../../Enums/LegacyRedirection";
import ChevronRight from "../../components/Atomos/Icons/ChevronRight";
type TabKey = "all" | "industry" | "specialty" | "private" | "practice";

interface IGroupDetails extends Omit<IDetail, "date" | "legacyId"> {
  date: string;
  link: string;
  regionName?: string;
  legacyId: string;
  groupId: string;
}

interface IGroup {
  id: string;
  legacyId: string;
  title: string;
  type: GroupType;
  members: number;
  globalGroupId?: string;
  legacyGlobalId?: string;
  isMember: boolean;
  details: IDetail[];
  icon?: string;
  subgroups?: ISubgroup[];
}

interface ITab {
  key: TabKey;
  label: string;
  icon?: React.ReactNode;
}

const TAB_FILTERS: Record<TabKey, ((group: IGroup) => boolean) | null> = {
  all: null,
  industry: (g) => g.type === GROUP_TYPES.INDUSTRY,
  specialty: (g) =>
    g.type === GROUP_TYPES.ASSOCIATION || g.type === GROUP_TYPES.WSGOPEN,
  private: (g) => g.type === GROUP_TYPES.LISTSERV,
  practice: (g) => g.type === GROUP_TYPES.PRACTICE,
};

const GROUPS_TAB_QUERY_MAP: Record<string, TabKey> = {
  all: "all",
  practice: "practice",
  industry: "industry",
  private: "private",
  assoc: "specialty",
  specialty: "specialty",
};

const parseGroupsTabParam = (param: string | null): TabKey | null => {
  if (!param) {
    return null;
  }
  return GROUPS_TAB_QUERY_MAP[param.toLowerCase()] ?? null;
};


const LEGACY_GROUP_FILE_BASE = LEGACY_MEMBER_URL + "/files/groups/";

const isStaffRole = (role: string | undefined | null): boolean => {
  const key = normalizeRoleToKey(role);
  return key === "wsgStaff" || key === "wsgSuper";
};

const buildTabs = (staffView: boolean): ITab[] => [
  {
    key: "all",
    label: staffView ? "ALL GROUPS" : "ALL MY GROUPS",
    icon: <img src={AllMyGroupsIcon} alt="All My Groups" />,
  },
  {
    key: "practice",
    label: staffView ? "PRACTICE GROUPS" : "MY PRACTICE GROUPS",
    icon: <img src={PracticeGroupsIcon} alt="Practice Groups" />,
  },
  {
    key: "industry",
    label: staffView ? "INDUSTRY GROUPS" : "MY INDUSTRY GROUPS",
    icon: <img src={IndustryGroupsIcon} alt="Industry Groups" />,
  },
  {
    key: "specialty",
    label: "SPECIALTY GROUPS",
    icon: <img src={SpecialtyGroupsIcon} alt="Specialty Groups" />,
  },
  {
    key: "private",
    label: staffView ? "PRIVATE GROUPS" : "MY PRIVATE GROUPS",
    icon: <Lock />,
  },
];

const viewedMessageKey = (messageId: string, messageType: string) =>
  `${messageId}\0${messageType}`;

const encodePracticeGroupQuery = (title: string): string =>
  encodeURIComponent(title.replace(/ & /g, "~").replace(/ /g, "|"));

const GroupBox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [myGroups, setMyGroups] = useState<IGroup[]>([]);
  const [groupsByTab, setGroupsByTab] = useState<IGroup[]>([]);
  const [groupDetails, setGroupDetails] = useState<any[] | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [suppressGroupHoverId, setSuppressGroupHoverId] = useState<
    string | null
  >(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  /** Survives loggedPerson reference swaps; merged with server viewedMessages in isMessageViewed */
  const [viewedMessageKeys, setViewedMessageKeys] = useState<Set<string>>(
    () => new Set()
  );
  /** Clears "New" for pending-invite groups after the user opens them (no message rows to mark). */
  const [dismissedInvitationGroupIds, setDismissedInvitationGroupIds] =
    useState<Set<string>>(() => new Set());
  const { loggedPerson, tokenDecoded } = useContext(AuthContext);
  const { patchUserPreferences } = useUserPreferences();

  const isStaffUser =
    isStaffRole(tokenDecoded?.extension_WSG_Permissions) ||
    isStaffRole(loggedPerson?.role);
  const tabs = buildTabs(isStaffUser);

  const isFetchingGroups = useRef(false);
  const lastLoggedPersonId = useRef<string | undefined>();
  const initialTabFromQueryApplied = useRef(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const isMessageViewed = useCallback(
    (messageId: string, messageType: string): boolean => {
      const key = viewedMessageKey(messageId, messageType);
      if (viewedMessageKeys.has(key)) {
        return true;
      }
      if (!loggedPerson?.viewedMessages) {
        return false;
      }

      return loggedPerson.viewedMessages.some(
        (msg) => msg.messageId === messageId && msg.messageTpye === messageType
      );
    },
    [loggedPerson, viewedMessageKeys]
  );

  const groupShowsNewBadge = useCallback(
    (group: IGroup): boolean => {
      if (group.isMember === false) {
        return !dismissedInvitationGroupIds.has(group.id);
      }
      return group.details.some(
        (detail) => !isMessageViewed(detail.id, detail.type)
      );
    },
    [isMessageViewed, dismissedInvitationGroupIds]
  );

  const hasNewActivity = (tabKey: TabKey): boolean => {
    if (myGroups.length === 0) {
      return false;
    }

    const filterFn = TAB_FILTERS[tabKey];
    const filteredGroups = filterFn ? myGroups.filter(filterFn) : myGroups;

    return filteredGroups.some(groupShowsNewBadge);
  };

  const formatDate = (timestamp: number): string => {
    if (!timestamp) {
      return "";
    }
    const dateObj = new Date(timestamp * 1000);

    const day = dateObj.getDate();
    const month = dateObj.toLocaleDateString("en-US", { month: "long" });
    const time = dateObj
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${day} ${month} ${dateObj.getFullYear()} at ${time}`;
  };

  const buildUrl = (
    type: string,
    legacyId: string,
    groupType: string,
    globalId?: string
  ): string => {
    const baseUrls: Record<string, string> = {
      file: "/wsg_practiceresources.asp",
      forumMessage: "/wsg_practiceforums.asp",
    };

    const baseUrl = baseUrls[type];
    if (!baseUrl) {
      return "";
    }

    const isSpecialType =
      groupType === GROUP_TYPES.ASSOCIATION ||
      groupType === GROUP_TYPES.WSGOPEN;

    if (isSpecialType) {
      return `${baseUrl}?group=${legacyId}&type=${groupType}`;
    }
    return `${baseUrl}?globalid=${globalId}&type=${groupType}&group=${legacyId}`;
  };

  const processGroupDetails = useCallback(
    (
      details: IDetail[],
      groupId: string,
      groupType: string,
      legacyGlobalId: string
    ): IGroupDetails[] => {

      const sortedDetails = [...(details || [])].sort((a, b) => {
        return (b.date || 0) - (a.date || 0);
      });

      return sortedDetails.map(
        (detail: IDetail): IGroupDetails => ({
          id: detail.id,
          title: detail.title || detail.name || "",
          documentName: detail.documentName ?? null,
          date: formatDate(detail.date),
          type: detail.type,
          author: detail.author || "",
          regionName: detail.regionName,
          legacyId: detail.legacyId,
          groupId,
          link: buildUrl(
            detail.type,
            detail.legacyId,
            groupType,
            legacyGlobalId
          ),
        })
      );
    },
    []
  );

  const processGroup = useCallback(
    (group: MyWSGGroupData): any => ({
      ...group,
      details: processGroupDetails(
        group.details || [],
        group.id,
        group.type,
        group.legacyGlobalId || ""
      ),
    }),
    [processGroupDetails]
  );

  const getLatestGroupDate = useCallback(
    (groupId: string, groupsWithDetails: MyWSGGroupData[]): number => {
      const group = groupsWithDetails.find((g) => g.id === groupId);
      return group?.details?.[0]?.date || 0;
    },
    []
  );

  const sortGroups = useCallback(
    (groups: any[], groupsWithDetails: MyWSGGroupData[]): any[] => {
      return groups.sort((a, b) => {
        if (a.isMember === false && b.isMember !== false) {
          return -1;
        }
        if (a.isMember !== false && b.isMember === false) {
          return 1;
        }
        if (a.isMember === false && b.isMember === false) {
          return 0;
        }

        const aLatestDate = getLatestGroupDate(a.id, groupsWithDetails);
        const bLatestDate = getLatestGroupDate(b.id, groupsWithDetails);

        if (aLatestDate && bLatestDate) {
          return bLatestDate - aLatestDate;
        }

        if (aLatestDate && !bLatestDate) {
          return -1;
        }
        if (!aLatestDate && bLatestDate) {
          return 1;
        }

        return 0;
      });
    },
    [getLatestGroupDate]
  );

  const initializeFirstGroup = useCallback((sortedGroups: any[]): void => {
    if (sortedGroups.length > 0) {
      setActiveGroupId(sortedGroups[0].id);
      setGroupDetails(sortedGroups[0].details || []);
    }
  }, []);

  const getPersonGroups = useCallback(
    async (personId: string): Promise<void> => {
      if (isFetchingGroups.current) {
        return;
      }

      isFetchingGroups.current = true;
      try {
        const groupsWithDetails =
          await getGroupsByPersonIdWithDetails(personId);

        const processedGroups = groupsWithDetails.map(processGroup);
        const sortedGroups = sortGroups(processedGroups, groupsWithDetails);

        setMyGroups(sortedGroups);
        setGroupsByTab(sortedGroups);
        initializeFirstGroup(sortedGroups);
      } catch (error) {
        console.error("Error loading groups:", error);
        setMyGroups([]);
        setGroupsByTab([]);
      } finally {
        isFetchingGroups.current = false;
      }
    },
    [processGroup, sortGroups, initializeFirstGroup]
  );

  useEffect(() => {
    if (!loggedPerson || !loggedPerson.id) {
      return;
    }

    if (lastLoggedPersonId.current === loggedPerson.id) {
      return;
    }

    lastLoggedPersonId.current = loggedPerson.id;
    getPersonGroups(loggedPerson.id);
  }, [loggedPerson, getPersonGroups]);

  useEffect(() => {
    const ids = loggedPerson?.preferences?.myWSGDismissedInviteGroupIds;
    setDismissedInvitationGroupIds(
      Array.isArray(ids) && ids.length > 0 ? new Set(ids) : new Set()
    );
  }, [
    loggedPerson?.id,
    loggedPerson?.preferences?.myWSGDismissedInviteGroupIds,
  ]);

  const handleTabChange = useCallback((tab: TabKey): void => {
    setActiveTab(tab);

    const filterFn = TAB_FILTERS[tab];
    const filteredGroups = filterFn ? myGroups.filter(filterFn) : myGroups;

    setGroupsByTab(filteredGroups);

    if (filteredGroups.length > 0) {
      setActiveGroupId(filteredGroups[0].id);
      setGroupDetails(filteredGroups[0].details || []);
    } else {
      setActiveGroupId(null);
      setGroupDetails(null);
    }
  }, [myGroups]);

  useEffect(() => {
    if (initialTabFromQueryApplied.current || myGroups.length === 0) {
      return;
    }

    const tabFromQuery = parseGroupsTabParam(searchParams.get("groups"));
    if (!tabFromQuery) {
      return;
    }

    initialTabFromQueryApplied.current = true;
    handleTabChange(tabFromQuery);
    document
      .getElementById("mywsg-groups")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [myGroups, searchParams, handleTabChange]);

  const markMessagesViewed = useCallback(
    async (
      groupId: string,
      messages: { messageId: string; messageType: string }[]
    ): Promise<void> => {
      const personId = loggedPerson?.id;
      if (!personId || messages.length === 0) {
        return;
      }
      const keys = messages.map((m) =>
        viewedMessageKey(m.messageId, m.messageType)
      );
      const messageIdSet = new Set(messages.map((m) => m.messageId));
      setViewedMessageKeys((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return next;
      });
      setGroupDetails(
        (prevDetails) =>
          prevDetails?.filter((detail) => !messageIdSet.has(detail.id)) ?? []
      );
      setMyGroups((prevGroups) =>
        prevGroups.map((group) => {
          if (group.id !== groupId) {
            return group;
          }
          return {
            ...group,
            details: group.details.filter(
              (detail) => !messageIdSet.has(detail.id)
            ),
          };
        })
      );
      setGroupsByTab((prevGroups) =>
        prevGroups.map((group) => {
          if (group.id !== groupId) {
            return group;
          }
          return {
            ...group,
            details: group.details.filter(
              (detail) => !messageIdSet.has(detail.id)
            ),
          };
        })
      );
      try {
        await Promise.all(
          messages.map((m) =>
            markMessageAsViewed(personId, m.messageId, m.messageType)
          )
        );
        try {
          const fresh = await getPersonById(personId);
          EventBus.dispatch(EventBusValues.UpdateLoggedPerson, {
            ...loggedPerson,
            viewedMessages: fresh.viewedMessages ?? [],
          });
        } catch (syncErr) {
          console.error(
            "Failed to refresh person after mark viewed:",
            syncErr
          );
          if (loggedPerson.viewedMessages) {
            for (const m of messages) {
              const exists = loggedPerson.viewedMessages.some(
                (msg) =>
                  msg.messageId === m.messageId &&
                  msg.messageTpye === m.messageType
              );
              if (!exists) {
                loggedPerson.viewedMessages.push({
                  messageId: m.messageId,
                  messageTpye: m.messageType,
                });
              }
            }
          } else {
            loggedPerson.viewedMessages = messages.map((m) => ({
              messageId: m.messageId,
              messageTpye: m.messageType,
            }));
          }
        }
      } catch (error) {
        console.error("Error marking messages as viewed:", error);
        setViewedMessageKeys((prev) => {
          const next = new Set(prev);
          keys.forEach((k) => next.delete(k));
          return next;
        });
        void getPersonGroups(personId);
      }
    },
    [loggedPerson, getPersonGroups]
  );

  const handleGroupClick = (groupId: string): void => {
    setActiveGroupId(groupId);

    const fromTab = groupsByTab.find((g) => g.id === groupId);
    const fromAll = myGroups.find((g) => g.id === groupId);
    const tabLen = fromTab?.details?.length ?? 0;
    const allLen = fromAll?.details?.length ?? 0;
    const selectedGroup =
      (tabLen >= allLen ? fromTab : fromAll) ?? fromTab ?? fromAll;
    const details = selectedGroup?.details ?? [];

    setGroupDetails(details);

    if (!selectedGroup) {
      return;
    }

    if (selectedGroup.isMember === false) {
      setDismissedInvitationGroupIds((prev) => new Set(prev).add(groupId));
      if (!loggedPerson?.id) {
        return;
      }
      const existing =
        loggedPerson.preferences?.myWSGDismissedInviteGroupIds ?? [];
      const merged = [...new Set([...existing, groupId])];
      void (async () => {
        try {
          const updated = await patchUserPreferences({
            preferences: {
              ...loggedPerson.preferences,
              myWSGDismissedInviteGroupIds: merged,
            },
          });
          EventBus.dispatch(EventBusValues.UpdateLoggedPerson, {
            ...loggedPerson,
            preferences: updated.preferences ?? {
              ...loggedPerson.preferences,
              myWSGDismissedInviteGroupIds: merged,
            },
          });
        } catch (e) {
          console.error("Failed to persist dismissed invite preference:", e);
        }
      })();
      return;
    }

    if (!loggedPerson?.id) {
      return;
    }

    const unviewed = details.filter(
      (d) => !isMessageViewed(d.id, d.type)
    );
    if (unviewed.length === 0) {
      return;
    }

    void markMessagesViewed(
      groupId,
      unviewed.map((d) => ({ messageId: d.id, messageType: d.type }))
    );
  };

  const handleMessageClick = async (
    groupId: string,
    messageId: string,
    messageTpye: string
  ): Promise<void> => {
    await markMessagesViewed(groupId, [
      { messageId, messageType: messageTpye },
    ]);
  };

  const groupUrl = (groupId: string | null): string => {
    if (!groupId) {
      return "";
    }
    return myGroups.find((g) => g.id === groupId)?.legacyId || "";
  };

  const renderActivityPanel = (): React.ReactNode => {
    const activeGroup = myGroups.find((g) => g.id === activeGroupId);

    if (!activeGroup) {
      return null;
    }

    if (activeGroup.isMember === false) {
      return (
        <div className="py-6 w-full">
          <p className="text-gray-font font-semibold bold mb-2">
            You have a pending invitation to this group
          </p>
          <p className="text-sm text-gray-font">
            Accept the invitation to view and participate in group activities
          </p>
        </div>
      );
    }

    if (groupDetails?.length) {
      return groupDetails.map((detail, idx) => (
        <Link
          to={detail.link}
          className="py-6 hover:bg-gray-50 transition-colors"
          key={idx}
          onClick={(e) => {
            e.preventDefault();
            void (async () => {
              await handleMessageClick(activeGroupId!, detail.id, detail.type);
              navigate(detail.link);
            })();
          }}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-gray-font">
              <span className="font-semibold">{detail.author}</span>
              {detail.type === "forumMessage"
                ? " posted a message"
                : " added a file"}
              :{" "}
              {detail.type === "file" ? (
                <span
                  className="font-semibold cursor-pointer"
                  onClick={async (e) => {
                    const fileName = detail.documentName?.trim();
                    if (!fileName) {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    await handleMessageClick(
                      activeGroupId!,
                      detail.id,
                      detail.type
                    );
                    window.open(
                      `${LEGACY_GROUP_FILE_BASE}${encodeURIComponent(fileName)}`,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  {detail.title}
                </span>
              ) : (
                <span className="font-semibold">{detail.title}</span>
              )}
            </p>
          </div>
          <div className="text-sm text-gray-500 mt-1 flex gap-2 items-center">
            <span>Published on {detail.date}</span>
            {!isMessageViewed(detail.id, detail.type) && (
              <span className="text-xs px-2 py-1 rounded-md bg-primary text-white">
                New
              </span>
            )}
            {detail.regionName && (
              <span className="text-xs px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-font">
                {detail.regionName}
              </span>
            )}
          </div>
        </Link>
      ));
    }

    return (
      <Link
        to={`/wsg_practicemembers.asp?group=${groupUrl(activeGroupId)}&globalid=${activeGroup.legacyGlobalId}`}
        className="py-6 w-full"
      >
        <p className="text-gray-font font-semibold mb-2">
          No recent activity in this group.
        </p>
        <p className="text-sm text-gray-font">
          Click here to be the first to share something in this group.
        </p>
      </Link>
    );
  };

  return (
    <div id="mywsg-groups" className="w-full text-left bg-white">
      <div className="text-white bg-primary text-xl md:text-2xl font-decimal font-medium p-5">
        {isStaffUser ? "GROUPS" : "MY GROUPS"}
      </div>
      <div className="p-6 min-h-[32rem]">
        <div className="lg:hidden pb-2">
          <CustomScrollContainer direction="horizontal" showScrollbar="auto">
            <div className="flex">
              {tabs.map((t) => {
                const isActive = t.key === activeTab;
                return (
                  <button
                    key={t.key}
                    onClick={() => handleTabChange(t.key)}
                    className={
                      "relative py-2 px-4 border-0 text-sm text-gray-font whitespace-nowrap flex-shrink-0 transition-all duration-200 hover:text-primary after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:transition-all after:duration-200 after:ease-in-out" +
                      (isActive
                        ? " font-bold after:h-0.5 after:bg-primary"
                        : " font-medium after:h-0 after:bg-primary")
                    }
                  >
                    <span className="flex items-center justify-center gap-2">
                      <span>{t.label}</span>
                      <span className="relative w-4 h-4">
                        {t.icon}
                        {hasNewActivity(t.key) && (
                          <span className="absolute -top-[.5rem] -right-[.5rem] w-2 h-2 rounded-full bg-primary" />
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </CustomScrollContainer>
        </div>

        <div className="hidden lg:flex justify-between pb-2">
          {tabs.map((t) => {
            const isActive = t.key === activeTab;
            return (
              <button
                key={t.key}
                onClick={() => handleTabChange(t.key)}
                className={
                  "relative py-2 px-4 border-0 text-sm md:text-base text-gray-font whitespace-nowrap flex-shrink-0 transition-all duration-200 hover:text-primary after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:transition-all after:duration-200 after:ease-in-out" +
                  (isActive
                    ? " font-bold after:h-0.5 after:bg-primary"
                    : " font-medium after:h-0 after:bg-primary")
                }
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t.label}</span>
                  <span className="relative w-4 h-4">
                    {t.icon}
                    {hasNewActivity(t.key) && (
                      <span className="absolute -top-[.5rem] -right-[.5rem] w-2 h-2 rounded-full bg-primary" />
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        {groupsByTab.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[25rem] text-center py-12">
            <Link
              to="/wsg_groups.asp"
              className="inline-block px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
            >
              Browse Practice Groups
            </Link>
          </div>
        ) : (
          <div className="mt-2 flex flex-col md:flex-row gap-4 md:overflow-hidden">
            <CustomScrollContainer
              direction={isMobile ? "horizontal" : "vertical"}
              showScrollbar="auto"
              className="flex md:flex-col gap-4 md:h-[25rem]"
              containerClassName="md:w-1/2 lg:w-[23rem] md:flex-shrink-0"
            >
              {groupsByTab.map((g) => {
                const isActive = g.id === activeGroupId;
                const suppressGroupHover = suppressGroupHoverId === g.id;

                return (
                  <a
                    href={`/wsg_practicegroupregions.asp?group=${encodePracticeGroupQuery(g.title)}`}
                    key={g.id}
                    className={`p-2 flex items-center gap-4 border rounded-md transition-all flex-shrink-0 w-fit md:w-full overflow-visible ${suppressGroupHover ? "" : "group"
                      } ${isActive
                        ? "border-primary border-2 bg-gray-50 duration-300"
                        : suppressGroupHover
                          ? "border-primary-light"
                          : "border-primary-light hover:border-primary/50"
                      }`}
                  >
                    <div className="hidden md:flex w-14 h-14 flex-shrink-0 items-center justify-center p-1 overflow-visible group-hover:scale-[105%]">
                      {g.icon ? (
                        <GroupIcon groupId={g.id} iconUrl={g.icon} />
                      ) : (
                        <span className="flex h-full w-full items-center justify-center text-2xl text-primary font-bold rounded-full">
                          <WorldServicesGroupIcon />
                        </span>
                      )}
                    </div>
                    <div className="flex-1 w-full flex md:block">
                      <span className="md:text-lg font-semibold line-clamp-2 text-gray-font  hover:text-primary-dark group-hover:text-primary-dark"
                      >
                        {g.title}
                      </span>
                      <div className="mt-0 md:mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-gray-700 group-hover:bg-primary-light group-hover:text-primary-dark">
                          {g.members} members
                        </span>


                        {groupShowsNewBadge(g) && (
                          <span className="text-xs px-2 py-1 rounded-md bg-primary text-white">
                            New
                          </span>
                        )}

                      </div>
                    </div>
                    <button
                      type="button"
                      className="text-xs flex font-semibold h-full items-end  rounded-md text-gray-font hover:text-primary"
                      onMouseEnter={() => setSuppressGroupHoverId(g.id)}
                      onMouseLeave={() => setSuppressGroupHoverId(null)}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleGroupClick(g.id);
                      }}
                    >
                      Group Activity
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    {/* </button> */}
                  </a>
                );
              })}
            </CustomScrollContainer>

            <CustomScrollContainer
              direction="vertical"
              showScrollbar="auto"
              className="flex flex-col h-[25rem] divide-y divide-gray-300 mt-4 md:mt-0"
              containerClassName="md:flex-1 md:min-w-0"
            >
              {renderActivityPanel()}
            </CustomScrollContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupBox;
